import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { FaBus, FaIdCard, FaTag } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const VEHICLE_TYPES = ["AC", "Non-AC", "Other"];

function formFromVehicle(vehicle) {
  const imageUrl = String(vehicle?.vehicle_image_url ?? "").trim();
  return {
    vehicle_id: String(vehicle?.vehicle_id || ""),
    vehicle_number: String(vehicle?.vehicle_number || ""),
    vehicle_name: String(vehicle?.vehicle_name || ""),
    vehicle_type: String(vehicle?.vehicle_type || ""),
    vehicle_image_url:
      !imageUrl || /^null$/i.test(imageUrl) ? "Null" : imageUrl,
  };
}

const MdlEditTransportVehicles = ({ actionClose, vehicle, onSuccess }) => {
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => formFromVehicle(vehicle));

  useEffect(() => {
    setShow(true);
    setForm(formFromVehicle(vehicle));
    setSaving(false);
  }, [vehicle]);

  const typeOptions = useMemo(() => {
    const current = String(form.vehicle_type || "").trim();
    if (current && !VEHICLE_TYPES.includes(current)) {
      return [current, ...VEHICLE_TYPES];
    }
    return VEHICLE_TYPES;
  }, [form.vehicle_type]);

  const handleClose = useCallback(() => {
    if (saving) return;
    setShow(false);
    if (typeof actionClose === "function") actionClose();
  }, [actionClose, saving]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const vehicleId = form.vehicle_id || vehicle?.vehicle_id;
    if (!vehicleId) {
      myToast.notify_warning("Vehicle id is missing.");
      return;
    }

    const vehicleName = String(form.vehicle_name || "").trim();
    const vehicleNumber = String(form.vehicle_number || "").trim();
    const vehicleType = String(form.vehicle_type || "").trim();

    if (!vehicleName || !vehicleNumber || !vehicleType) {
      myToast.notify_warning("Please fill all fields.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }

    const imageUrl =
      !form.vehicle_image_url || /^null$/i.test(String(form.vehicle_image_url))
        ? "Null"
        : String(form.vehicle_image_url);

    setSaving(true);
    try {
      const params = {
        school_code: schoolCode,
        vehicle_id: vehicleId,
        vehicle_name: vehicleName,
        vehicle_number: vehicleNumber,
        vehicle_type: vehicleType,
        vehicle_image_url: imageUrl,
      };

      const response = await axios.get(`${API_BASE()}updateVehicleDetails.php`, {
        params,
      });
      const { api_status, api_remark } = response.data || {};

      if (api_status === "OK") {
        const updated = {
          ...vehicle,
          ...form,
          vehicle_id: vehicleId,
          vehicle_name: vehicleName,
          vehicle_number: vehicleNumber,
          vehicle_type: vehicleType,
          vehicle_image_url: imageUrl,
        };
        myToast.notify_success("Vehicle updated successfully");
        setShow(false);
        if (typeof onSuccess === "function") onSuccess(updated);
        if (typeof actionClose === "function") actionClose();
      } else {
        myToast.notify_warning(api_remark || "Could not update vehicle");
      }
    } catch (error) {
      console.error("[MdlEditTransportVehicles] update error", error);
      myToast.notify_error("Failed to update vehicle");
    } finally {
      setSaving(false);
    }
  }, [form, vehicle, actionClose, onSuccess]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      centered
      size="lg"
      className="mdl-add-route"
      backdrop={saving ? "static" : true}
      keyboard={!saving}
    >
      <Modal.Header closeButton={!saving} className="mdl-add-route__header">
        <Modal.Title className="mdl-add-route__title">
          <span className="mdl-add-route__icon" aria-hidden>
            <FaBus />
          </span>
          Edit Vehicle
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mdl-add-route__body">
        {saving ? (
          <div className="mdl-add-route__loading">
            <MyLoader />
          </div>
        ) : (
          <div className="mdl-add-route__form">
            <div className="mdl-add-route__row">
              <div className="mdl-add-route__field">
                <label htmlFor="mdl-edit-vehicle-name">
                  <FaBus aria-hidden /> Vehicle name
                </label>
                <input
                  id="mdl-edit-vehicle-name"
                  name="vehicle_name"
                  type="text"
                  placeholder="e.g. School Bus 01"
                  value={form.vehicle_name}
                  onChange={handleChange}
                  autoFocus
                />
              </div>
              <div className="mdl-add-route__field">
                <label htmlFor="mdl-edit-vehicle-number">
                  <FaIdCard aria-hidden /> Vehicle number
                </label>
                <input
                  id="mdl-edit-vehicle-number"
                  name="vehicle_number"
                  type="text"
                  placeholder="e.g. MH12 AB 1234"
                  value={form.vehicle_number}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mdl-add-route__field">
              <label htmlFor="mdl-edit-vehicle-type">
                <FaTag aria-hidden /> Vehicle type
              </label>
              <select
                id="mdl-edit-vehicle-type"
                name="vehicle_type"
                value={form.vehicle_type}
                onChange={handleChange}
              >
                <option value="">Select type</option>
                {typeOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="mdl-add-route__footer">
        <Button
          variant="light"
          onClick={handleClose}
          disabled={saving}
          className="mdl-add-route__cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="mdl-add-route__submit"
          style={{
            background: COLORS.primary_color_shade5,
            borderColor: COLORS.primary_color_shade7,
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlEditTransportVehicles;
